import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  makeStyles,
  TextField,
  Typography,
  MenuItem,
} from "@material-ui/core";
import { Html5Qrcode } from "html5-qrcode";
import { useSnackbar } from "notistack";
import { useAuthState } from "../../context/auth";
import { lookupQrCode, createQrPairing } from "../../api/qrCodes";

const useStyles = makeStyles((theme) => ({
  reader: {
    width: "100%",
    maxWidth: 400,
    margin: "0 auto",
  },
  scannedContent: {
    wordBreak: "break-all",
    padding: theme.spacing(1),
    backgroundColor: theme.palette.grey[100],
    borderRadius: 4,
    marginBottom: theme.spacing(2),
  },
}));

function parseFieldIdFromQr(text) {
  const match = text.match(/agri-tech\.co\.uk\/fields?\/(\d+)/);
  return match ? match[1] : null;
}

const QrScannerDialog = ({
  open,
  onClose,
  onFieldFound,
  fixedFieldId,
  fixedFieldName,
  onPairingChange,
}) => {
  const classes = useStyles();
  const scannerRef = useRef(null);
  const { enqueueSnackbar } = useSnackbar();
  const { token, client } = useAuthState();
  const [scannedContent, setScannedContent] = useState(null);
  const [selectedFieldId, setSelectedFieldId] = useState(
    fixedFieldId != null ? String(fixedFieldId) : ""
  );
  const isFixedFieldMode = fixedFieldId != null;
  const targetFieldId = isFixedFieldMode
    ? String(fixedFieldId)
    : selectedFieldId;

  const resetState = () => {
    setScannedContent(null);
    setSelectedFieldId(isFixedFieldMode ? String(fixedFieldId) : "");
  };

  useEffect(() => {
    if (isFixedFieldMode) {
      setSelectedFieldId(String(fixedFieldId));
    }
  }, [fixedFieldId, isFixedFieldMode]);

  const handleClose = () => {
    resetState();
    onClose();
  };

  useEffect(() => {
    if (!open || scannedContent) return;

    let mounted = true;
    const scannerId = "dashboard-qr-reader";

    const getCameraErrorMessage = (error) => {
      const message = error instanceof Error ? error.message : "";
      if (!window.isSecureContext) {
        return "Camera access requires HTTPS";
      }
      if (!navigator.mediaDevices?.getUserMedia) {
        return "This browser does not support camera access";
      }
      if (
        message.includes("Permission") ||
        message.includes("NotAllowedError") ||
        message.includes("permission")
      ) {
        return "Camera permission was denied";
      }
      if (
        message.includes("NotFoundError") ||
        message.includes("Requested device not found") ||
        message.includes("No cameras found")
      ) {
        return "No camera was found on this device";
      }
      return "Could not access camera";
    };

    const startScannerWithConfig = async (scanner, cameraConfig) => {
      await scanner.start(
        cameraConfig,
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (decodedText) => {
          scanner.stop().catch(() => {});
          handleScan(decodedText);
        },
        () => {}
      );
    };

    const startScannerWithFallbacks = async (scanner) => {
      const attempts = [
        () => startScannerWithConfig(scanner, { facingMode: "environment" }),
      ];

      const cameras = await Html5Qrcode.getCameras().catch(() => []);
      if (cameras.length > 0) {
        const preferredCamera =
          cameras.find((camera) =>
            /(back|rear|environment)/i.test(camera.label)
          ) ?? cameras[0];
        attempts.unshift(() =>
          startScannerWithConfig(scanner, preferredCamera.id)
        );
      }

      let lastError;
      for (const attempt of attempts) {
        try {
          await attempt();
          return;
        } catch (error) {
          lastError = error;
        }
      }
      throw lastError;
    };

    const startScanner = async () => {
      await new Promise((r) => setTimeout(r, 300));
      if (!mounted) return;

      const el = document.getElementById(scannerId);
      if (!el) return;

      const scanner = new Html5Qrcode(scannerId);
      scannerRef.current = scanner;

      try {
        await startScannerWithFallbacks(scanner);
      } catch (error) {
        enqueueSnackbar(getCameraErrorMessage(error), { variant: "error" });
        handleClose();
      }
    };

    const handleScan = async (text) => {
      // 1. Check if it's a direct app URL
      if (!isFixedFieldMode) {
        const fieldId = parseFieldIdFromQr(text);
        if (fieldId) {
          onFieldFound(fieldId);
          handleClose();
          return;
        }
      }

      // 2. Try API lookup
      if (token && client?.id) {
        try {
          const res = await lookupQrCode(token, client.id, text);
          if (res.status === 1 && res.data?.field_id) {
            const pairedFieldId = String(res.data.field_id);
            if (isFixedFieldMode) {
              if (pairedFieldId === String(fixedFieldId)) {
                enqueueSnackbar("QR code is already paired to this field", {
                  variant: "info",
                });
              } else {
                enqueueSnackbar(
                  `QR code is already paired to ${res.data.field?.name ?? "another field"}`,
                  { variant: "warning" }
                );
              }
              handleClose();
              return;
            }
            onFieldFound(pairedFieldId);
            handleClose();
            return;
          }
        } catch (e) {
          if (e?.response?.status === 404) {
            setScannedContent(text);
            return;
          }
          enqueueSnackbar(
            e?.response?.data?.message || "Could not look up QR code",
            { variant: "error" }
          );
          handleClose();
          return;
        }
      }

      // 3. Show pairing UI
      setScannedContent(text);
    };

    startScanner();

    return () => {
      mounted = false;
      if (scannerRef.current?.isScanning) {
        scannerRef.current.stop().catch(() => {});
      }
      scannerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, scannedContent]);

  const handlePair = async () => {
    if (!token || !client?.id || !scannedContent || !targetFieldId) return;
    try {
      await createQrPairing(token, client.id, {
        content: scannedContent,
        field_id: Number(targetFieldId),
      });
      enqueueSnackbar("QR code paired successfully", { variant: "success" });
      onPairingChange?.();
      if (!isFixedFieldMode) {
        onFieldFound(targetFieldId);
      }
      handleClose();
    } catch (e) {
      const message =
        e?.response?.data?.message || "Failed to pair QR code";
      enqueueSnackbar(message, { variant: "error" });
    }
  };

  const fields = client?.fields ?? [];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {scannedContent ? "Pair QR Code" : "Scan QR Code"}
      </DialogTitle>
      <DialogContent>
        {scannedContent ? (
          <>
            <Typography variant="subtitle2" gutterBottom>
              Scanned content:
            </Typography>
            <Typography className={classes.scannedContent} variant="body2">
              {scannedContent}
            </Typography>
            {isFixedFieldMode ? (
              <TextField
                label="Pair with field"
                value={fixedFieldName ?? `Field ${fixedFieldId}`}
                fullWidth
                variant="outlined"
                size="small"
                InputProps={{ readOnly: true }}
              />
            ) : (
              <TextField
                select
                label="Pair with field"
                value={selectedFieldId}
                onChange={(e) => setSelectedFieldId(e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
              >
                {fields.map((f) => (
                  <MenuItem key={f.id} value={String(f.id)}>
                    {f.name} {f.farm ? `(${f.farm})` : ""}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </>
        ) : (
          <div id="dashboard-qr-reader" className={classes.reader} />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="default">
          Cancel
        </Button>
        {scannedContent && (
          <Button
            onClick={handlePair}
            color="primary"
            variant="contained"
            disabled={!targetFieldId}
          >
            {isFixedFieldMode ? "Pair QR Code" : "Pair & Open Field"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default QrScannerDialog;
